/**
 * Swamithra — the shared query pool as an Expert works it (US-01-001, US-01-003).
 *
 * ONE POOL, NO ASSIGNMENT. Every authenticated Expert sees every query; there is
 * no per-Expert filter and no claim step, which is why no function here takes an
 * expert id (US-01-001.1).
 *
 * WHAT AN EXPERT MAY CHANGE: the category, and nothing else. There is no
 * function to edit a title, a description, an attachment or the user's notes,
 * and none to delete or export — the API rejects those writes and this module
 * offers no way to attempt them (US-01-001.19, US-00-006.12).
 */

import { type QueryCategory } from "../domain/categories"
import { referenceParam } from "../domain/ids"
import { type PageParams, pageQuery, paginated } from "../domain/pagination"
import {
	expertCategoryUpdateSchema,
	expertQueryDetailSchema,
	type ExpertQueryFilters,
	expertQueryRowSchema,
	type ExpertQuerySort,
	expertQueryStateSchema,
	type ExpertResponseInput,
	expertResponseInputSchema,
	queryMessageSchema,
} from "../domain/query"
import { type SendFromDraft } from "../domain/response-draft"
import { appendFiles, type PlatformFile } from "../files"
import { api } from "./client"
import { type ReadOptions, type SubmitOptions } from "./options"
import { appendDraftSend } from "./panel-drafts"

const expertQueryPage = paginated(expertQueryRowSchema)

/**
 * The All-Queries dashboard.
 *
 * Status, category and date range compose — a query has to match all three
 * (US-01-001.2) — and sorting applies across the whole result set rather than
 * the current page (US-01-001.4), both of which are the server's job because
 * only the server can see the rows that are not on this page.
 */
export function fetchExpertQueries(
	filters: ExpertQueryFilters = {},
	page: PageParams = {},
	options: ReadOptions = {},
) {
	return api.get("/expert/queries", expertQueryPage, {
		...options,
		query: {
			...pageQuery(page),
			...(filters.status ? { status: filters.status } : {}),
			...(filters.category ? { category: filters.category } : {}),
			// The Date Range control, as the service names it. Sent as
			// `submittedFrom`/`submittedTo` these were simply not applied, and a
			// filter that is ignored rather than refused is a dashboard quietly
			// showing the wrong fortnight (US-01-001.2).
			...(filters.submittedFrom ? { from: filters.submittedFrom } : {}),
			...(filters.submittedTo ? { to: filters.submittedTo } : {}),
			// One parameter for name, title, `#12` and a mobile number in any
			// format (US-01-001.3). The server normalises a number to E.164 before
			// matching, so `98765 43210` and `+919876543210` find the same queries.
			...(filters.search ? { search: filters.search } : {}),
			...expertSortQuery(filters.sort),
		},
	})
}

/**
 * A sort selection as the service's two parameters.
 *
 * The panel expresses a sort as one value with a `-` for descending; the service
 * reads a column and a direction. The DEFAULT IS OLDEST SUBMITTED FIRST: an
 * unconfigured dashboard is a work queue, and the row that has been waiting
 * longest against the 24-hour SLA is the one an Expert should be looking at
 * (US-01-001.4/.11). Left unsent, the service defaults to newest first — the
 * opposite — so the default is stated here rather than assumed.
 */
function expertSortQuery(sort: ExpertQuerySort | undefined): Record<string, string> {
	const selection = sort ?? "submittedAt"
	const descending = selection.startsWith("-")
	const column = descending ? selection.slice(1) : selection
	// The COLUMN is a VALUE, not a field name, so ./client's parameter mapping
	// does not reach it and the service's spelling has to be written out. It
	// takes `submitted` or `answered` — bare, with no `_at` — and treats
	// anything it does not recognise as `submitted`, which is how a sort by
	// answered time silently became a sort by submission.
	return {
		sort: column === "answeredAt" ? "answered" : "submitted",
		order: descending ? "desc" : "asc",
	}
}

export function fetchExpertQuery(queryRef: string, options: ReadOptions = {}) {
	return api.get(`/expert/queries/${referenceParam(queryRef)}`, expertQueryDetailSchema, options)
}

/**
 * Posts an Expert response into the thread.
 *
 * The rich-text response and any attachments are sent as multipart data. The
 * FIRST response stamps `firstResponseAt` and
 * moves the query to In Progress; a later one changes neither (US-01-001.6/.15),
 * and both notify the user.
 *
 * The idempotency key is what makes US-01-001.24 work: a send that failed on the
 * network and is retried posts exactly one message and sends one notification.
 */
export async function postExpertResponse(
	queryRef: string,
	input: ExpertResponseInput,
	attachments: readonly PlatformFile[],
	options: SubmitOptions,
	fromDraft?: SendFromDraft,
) {
	// `/responses`, which is the route the panel has. `/messages` is the USER's
	// composer and does not exist under /expert — a reply sent there was a 404,
	// so no Expert could answer anything.
	const parsed = expertResponseInputSchema.parse(input)
	const form = new FormData()
	form.append("body_rich", parsed.bodyRich)
	appendDraftSend(form, fromDraft)
	appendFiles(form, "attachments", attachments)

	return await api.post(
		`/expert/queries/${referenceParam(queryRef)}/responses`,
		queryMessageSchema,
		form,
		{ signal: options.signal, idempotencyKey: options.idempotencyKey },
	)
}

/**
 * Mark as Answered (US-01-001.7).
 *
 * Stamps `answeredAt`, makes the user's feedback prompt eligible and sends the
 * 'answered' notification. `answeredAt` is never overwritten by a later close
 * (US-01-003.14).
 */
export function markQueryAnswered(queryRef: string, options: SubmitOptions) {
	// The answer is the query's own state, NOT the detail: the service replies
	// with the row's fields and no user attached, because the caller is already
	// looking at the record it acted on. The caller refetches the detail.
	return api.post(
		`/expert/queries/${referenceParam(queryRef)}/answer`,
		expertQueryStateSchema,
		undefined,
		{ signal: options.signal, idempotencyKey: options.idempotencyKey },
	)
}

/**
 * Close Query (US-01-003.1).
 *
 * Offered ONLY on an Answered query. On an Open or In Progress one the panel
 * shows no control and a direct POST returns 409 with the status unchanged
 * (US-01-003.6); on an already-Closed one it returns 409 and the original
 * `closedAt` and `closeReason` stand (US-01-003.7).
 *
 * No notification of any kind is dispatched on a close, by any of the three
 * routes (US-01-003.11).
 */
export function closeQuery(queryRef: string, options: SubmitOptions) {
	return api.post(
		`/expert/queries/${referenceParam(queryRef)}/close`,
		expertQueryStateSchema,
		undefined,
		{ signal: options.signal, idempotencyKey: options.idempotencyKey },
	)
}

/**
 * Corrects a mis-tagged category (US-01-001.8).
 *
 * The only user-supplied field an Expert may change. The write produces an audit
 * row carrying the expert id, the previous and the new category and the
 * timestamp (US-01-001.20, US-00-006.13) — server-side, because an audit trail a
 * client could omit is not an audit trail.
 */
export async function updateQueryCategory(
	queryRef: string,
	category: QueryCategory,
	options: ReadOptions = {},
) {
	return await api.patch(
		`/expert/queries/${referenceParam(queryRef)}`,
		expertQueryStateSchema,
		expertCategoryUpdateSchema.parse({ category }),
		options,
	)
}
